import {
    ChevronDownIcon,
    ChevronRightIcon,
    EnvelopeIcon,
    MapPinIcon,
    PencilIcon,
    PlusIcon,
    TrashIcon,
    UserIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@/components/core/Button';
import { Icon } from '@/components/core/Icon';
import { IconButton } from '@/components/core/IconButton';
import { Input } from '@/components/core/Input';
import { Loader } from '@/components/core/Loader';
import { AUTH_OBJECT_TYPES, useAuth } from '@/hooks/useAuth';
import { useOrganization } from '@/hooks/useOrganization';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { getInjector, getRootScope } from '@/services/angularjs-bridge';

import {
    BusinessPartner,
    canLoginAsManagingUser,
    canShowDeleteContact,
    contactErrorMessage,
    groupContactsByLetter,
    hidesContactActions,
    partnersPageSize,
    UserOrganizationRef,
} from './businessPartner';
import { loginAsManagingUser, useBusinessPartners } from './businessPartner.queries';
import { ContactEditorDialog } from './ContactEditorDialog';
import { ContactPublications } from './ContactPublications';
import { setLocationHash } from './contactsTab';
import { DeleteContactDialog } from './DeleteContactDialog';

interface LoginContext {
    communityManager: boolean;
    legacyAccount: boolean;
    userOrganizations: UserOrganizationRef[];
}

const readLoginContext = (): LoginContext => {
    try {
        const root = getRootScope(getInjector());
        const organizations = root.allUserOrganizations;
        return {
            communityManager: Boolean(root.organization?.communityManager),
            legacyAccount: Boolean(root.user?.legacyAccount),
            userOrganizations: Array.isArray(organizations) ? organizations : [],
        };
    } catch {
        return { communityManager: false, legacyAccount: true, userOrganizations: [] };
    }
};

const BusinessPartners = () => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const { auth } = useAuth();
    const { organization } = useOrganization();
    const [limit] = useState(partnersPageSize);
    const [keyword, setKeyword] = useState('');
    const [debouncedKeyword, setDebouncedKeyword] = useState('');
    const [expandedIds, setExpandedIds] = useState<number[]>([]);
    const [editorContact, setEditorContact] = useState<BusinessPartner | undefined>();
    const [editorOpen, setEditorOpen] = useState(false);
    const [deleteContact, setDeleteContact] = useState<BusinessPartner | undefined>();
    const [loggingInId, setLoggingInId] = useState<number | undefined>();
    const communityManager = Boolean(organization?.communityManager);
    const loginContext = useMemo(readLoginContext, []);
    const sentinelRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const timer = window.setTimeout(() => setDebouncedKeyword(keyword.trim()), 300);
        return () => window.clearTimeout(timer);
    }, [keyword]);

    const partners = useBusinessPartners({
        variables: {
            limit,
            keywords: debouncedKeyword || undefined,
        },
    });
    const {
        data,
        isLoading,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
    } = partners;
    const contacts = useMemo(
        () => data?.pages.flatMap((page) => page.data) ?? [],
        [data],
    );
    const groups = useMemo(() => groupContactsByLetter(contacts), [contacts]);
    const canCreate = Boolean(auth?.hasAnyPermission(AUTH_OBJECT_TYPES.OBJECT_TYPE_CONTACT, 'create'));

    useEffect(() => {
        const node = sentinelRef.current;
        if (!node) {
            return undefined;
        }
        const observer = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting) && hasNextPage && !isFetchingNextPage) {
                void fetchNextPage();
            }
        });
        observer.observe(node);
        return () => observer.disconnect();
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    const contactPermission = (action: string, item: BusinessPartner) => {
        const contactAuth = auth as typeof auth & {
            hasContactPermission?: (permission: string, context: {
                contactRole?: string;
                customValues?: Record<string, unknown>;
            }) => boolean;
        };
        return Boolean(contactAuth?.hasContactPermission?.(action, {
            contactRole: item.contactRole,
            customValues: item.customValues,
        }));
    };

    const login = (item: BusinessPartner) => {
        if (item.organizationId == null || item.id == null) {
            return;
        }
        setLoggingInId(item.id);
        toast.info(t('CONTACTS.LOGIN_AS_MANAGING_USER', { organizationName: item.name ?? '' }));
        window.setTimeout(() => {
            loginAsManagingUser(item.organizationId as number)
                .then(() => {
                    window.location.assign('/');
                })
                .catch((error: unknown) => {
                    setLoggingInId(undefined);
                    const data = axios.isAxiosError(error) ? error.response?.data : undefined;
                    toast.error(contactErrorMessage(data, t, 'ERROR_OCCURRED') ?? t('ERROR_OCCURRED'));
                });
        }, 2000);
    };

    const showEmpty = !isLoading && contacts.length === 0;

    return (
        <div id="businessPartner" className="tw-flex tw-flex-col tw-gap-4">
            <div className="tw-flex tw-flex-col tw-gap-3 lg:tw-flex-row lg:tw-items-center">
                <Button
                    id="create-group-btn"
                    type="button"
                    hasIcon
                    icon={PlusIcon}
                    disabled={!canCreate}
                    onClick={() => {
                        setEditorContact(undefined);
                        setEditorOpen(true);
                    }}
                >
                    {t('CONTACTS.ADD_CONTACT')}
                </Button>
                <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-1 lg:tw-flex-row lg:tw-items-center lg:tw-gap-3">
                    <label
                        className="tw-shrink-0 tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base"
                        htmlFor="search-organization-input"
                    >
                        {t('SEARCH_BUSINESSES')}
                    </label>
                    <Input
                        id="search-organization-input"
                        className="tw-w-full"
                        placeholder={t('SEARCH_CONTACTS_LABEL')}
                        value={keyword}
                        onChange={(event) => setKeyword(event.target.value)}
                    />
                </div>
            </div>

            {isLoading && <Loader />}

            {showEmpty && (
                <div className="tw-flex tw-flex-col tw-items-center tw-gap-4 tw-py-10">
                    <span className="tw-flex tw-h-16 tw-w-16 tw-items-center tw-justify-center tw-rounded-full tw-bg-fill-secondary lg:tw-h-20 lg:tw-w-20">
                        <Icon icon={UserIcon} className="tw-h-8 tw-w-8 lg:tw-h-10 lg:tw-w-10" variant="dark" />
                    </span>
                    <h2 className="tw-text-center tw-text-t-lg tw-font-bold tw-text-h-primary lg:tw-text-t-xl">
                        {t('NODATA.CONTACTS_HEADLINE')}
                    </h2>
                    <Button type="button" onClick={() => setLocationHash('search')}>
                        {t('NODATA.DASHBOARD_BUSINESS_BUTTON')}
                    </Button>
                </div>
            )}

            {groups.map((group, groupIndex) => (
                <section key={group.letter}>
                    <h2
                        id={`contactSearchLetter${group.letter}`}
                        className="tw-mb-2 tw-text-t-lg tw-font-bold tw-text-h-primary lg:tw-text-t-xl"
                    >
                        {group.letter}
                    </h2>
                    <table id={groupIndex === 0 ? 'results-table' : undefined} className="tw-w-full">
                        <tbody>
                            {group.contacts.map((item, index) => {
                                const expanded = item.id != null && expandedIds.includes(item.id);
                                const showActions = !hidesContactActions(organization?.id, item.id);
                                const rowKey = item.id ?? `${group.letter}-${index}`;
                                return (
                                    <Fragment key={rowKey}>
                                        {index > 0 && (
                                            <tr>
                                                <td className="tw-p-0" colSpan={2}>
                                                    <div className="tw-h-px tw-w-full tw-bg-gray-200" />
                                                </td>
                                            </tr>
                                        )}
                                        <tr>
                                            <td className="tw-p-3" colSpan={2}>
                                                <div className="tw-flex tw-flex-col tw-gap-3 lg:tw-flex-row lg:tw-items-center lg:tw-gap-4">
                                                    <div className="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-full tw-bg-fill-secondary">
                                                        {item.imageUrl ? (
                                                            <img
                                                                src={item.imageUrl}
                                                                alt={item.name ?? ''}
                                                                className="tw-h-full tw-w-full tw-object-cover"
                                                            />
                                                        ) : (
                                                            <Icon icon={UserIcon} className="tw-h-5 tw-w-5" variant="secondary" />
                                                        )}
                                                    </div>
                                                    <div className="tw-grid tw-min-w-0 tw-flex-1 tw-grid-cols-1 tw-gap-2 lg:tw-grid-cols-2">
                                                        <div className="tw-min-w-0">
                                                            <div className="tw-break-words tw-text-t-base tw-font-bold tw-text-t-primary lg:tw-text-t-lg">
                                                                {item.name}
                                                            </div>
                                                            {item.gln && (
                                                                <div className="tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base">{item.gln}</div>
                                                            )}
                                                        </div>
                                                        <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-1 tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base">
                                                            <div className="tw-flex tw-items-start tw-gap-2">
                                                                <Icon icon={MapPinIcon} className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0 lg:tw-h-5 lg:tw-w-5" variant="secondary" />
                                                                <span className="tw-break-words">{item.address}</span>
                                                            </div>
                                                            <div className="tw-flex tw-items-start tw-gap-2">
                                                                <Icon icon={EnvelopeIcon} className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0 lg:tw-h-5 lg:tw-w-5" variant="secondary" />
                                                                <span className="tw-break-all">{item.email}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {showActions && (
                                                        <div className="tw-flex tw-shrink-0 tw-items-center tw-justify-end">
                                                            <IconButton
                                                                icon={PencilIcon}
                                                                size="md"
                                                                className="tw-icon-primary"
                                                                disabled={!contactPermission('update', item)}
                                                                title={t('CONTACT_MANAGEMENT.EDIT')}
                                                                aria-label={t('CONTACT_MANAGEMENT.EDIT')}
                                                                onClick={() => {
                                                                    setEditorContact(item);
                                                                    setEditorOpen(true);
                                                                }}
                                                            />
                                                            <IconButton
                                                                id="show-publications"
                                                                icon={expanded ? ChevronDownIcon : ChevronRightIcon}
                                                                size="md"
                                                                className="tw-icon-primary"
                                                                disabled={item.organizationId == null}
                                                                title={t('PUBLICATIONS')}
                                                                aria-label={t('PUBLICATIONS')}
                                                                onClick={() => {
                                                                    if (item.id == null || item.organizationId == null) {
                                                                        return;
                                                                    }
                                                                    setExpandedIds((current) => (
                                                                        current.includes(item.id as number)
                                                                            ? current.filter((id) => id !== item.id)
                                                                            : [...current, item.id as number]
                                                                    ));
                                                                }}
                                                            />
                                                            <IconButton
                                                                icon={EnvelopeIcon}
                                                                size="md"
                                                                className="tw-icon-primary"
                                                                title={t('CONTACTS.SEND_EMAIL')}
                                                                aria-label={t('CONTACTS.SEND_EMAIL')}
                                                                onClick={() => {
                                                                    window.location.href = `mailto:${item.email ?? ''}`;
                                                                }}
                                                            />
                                                            {canShowDeleteContact(communityManager, item.invitationType) && (
                                                                <IconButton
                                                                    icon={TrashIcon}
                                                                    size="md"
                                                                    className="tw-icon-primary"
                                                                    disabled={!contactPermission('delete', item)}
                                                                    title={t('CONTACTS.DELETE_CONTACT')}
                                                                    aria-label={t('CONTACTS.DELETE_CONTACT')}
                                                                    onClick={() => setDeleteContact(item)}
                                                                />
                                                            )}
                                                            {canLoginAsManagingUser({
                                                                communityManager,
                                                                legacyAccount: loginContext.legacyAccount,
                                                                userOrganizations: loginContext.userOrganizations,
                                                                contact: item,
                                                            }) && (
                                                                <IconButton
                                                                    icon={UserIcon}
                                                                    size="md"
                                                                    className="tw-icon-primary"
                                                                    disabled={!contactPermission('login', item) || loggingInId === item.id}
                                                                    title={t('CONTACTS.LOGIN_AS_MANAGING_USER', { organizationName: item.name ?? '' })}
                                                                    aria-label={t('CONTACTS.LOGIN_AS_MANAGING_USER', { organizationName: item.name ?? '' })}
                                                                    onClick={() => login(item)}
                                                                />
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                        {expanded && item.organizationId != null && (
                                            <tr>
                                                <td className="tw-px-3 tw-pb-3" colSpan={2}>
                                                    <ContactPublications organizationId={item.organizationId} />
                                                </td>
                                            </tr>
                                        )}
                                    </Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </section>
            ))}
            <div ref={sentinelRef} className="tw-h-1" />
            {isFetchingNextPage && <Loader />}

            <ContactEditorDialog
                open={editorOpen}
                contact={editorContact}
                onClose={() => setEditorOpen(false)}
            />
            <DeleteContactDialog
                open={deleteContact != null}
                contact={deleteContact}
                onClose={() => setDeleteContact(undefined)}
            />
        </div>
    );
};

export { BusinessPartners };
